/* eslint-disable */
import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  FormControlLabel,
  InputAdornment,
  Modal,
  Radio,
  RadioGroup,
  Stack,
  Typography,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { Add, CalendarMonth, Edit } from "@mui/icons-material";
import moment from "moment";
import { tableStyle } from "../../components";
import { CssTextField } from "../../components/TextField";
import Header from "../../components/Header";
import AvailabilityUpdation from "../../components/AvailabilityUpdation";
import {
  ACTIVATE_EARLY_BIRD_DISCOUNT,
  ADD_NEW_EARLY_BIRD_DISCOUNT,
  LIST_ALL_EARLY_BIRD_DISCOUNTS,
  LIST_EARLY_BIRD_DISCOUNT_DETAILS,
  UPDATE_EARLY_BIRD_DISCOUNT,
} from "../../graphql";
import { authorizedPost } from "../../store/actions";

const startIcon = {
  startAdornment: (
    <InputAdornment position="start">
      <CalendarMonth fontSize="small" sx={{ cursor: "pointer" }} />
    </InputAdornment>
  ),
};

const getInitialValues = () => ({
  minAdvanceBookingDays: "",
  startDate: moment().format("YYYY-MM-DD"),
  endDate: moment().add(1, "days").format("YYYY-MM-DD"),
  mode: "percentage",
  discountValue: "",
  fixedRate: "",
});

function RoomtypeEarlyBirdDiscount({ roomtypeId, colors }) {
  const startInputRef = useRef();
  const endInputRef = useRef();

  const [discounts, setDiscounts] = useState([]);
  const [openModal, setOpenModal] = useState(false);
  const [editingId, setEditingId] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [values, setValues] = useState(getInitialValues());

  const modalStyle = useMemo(
    () => ({
      position: "absolute",
      top: "50%",
      left: "50%",
      transform: "translate(-50%, -50%)",
      width: { sm: 520, xs: "92%" },
      bgcolor: colors.primary[400],
      outline: "none",
      boxShadow: 24,
      p: 2,
      borderRadius: 2,
      maxHeight: "90vh",
      overflowY: "auto",
    }),
    [colors]
  );

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    const response = await authorizedPost(LIST_ALL_EARLY_BIRD_DISCOUNTS, {
      roomtypeId,
      pageNumber: 1,
      pageSize: 100,
    });
    setDiscounts(response?.data?.list_all_early_bird_discounts?.data || []);
  };

  const closeModal = () => {
    setOpenModal(false);
    setEditingId("");
    setValues(getInitialValues());
  };

  const openForEdit = async (id) => {
    const response = await authorizedPost(LIST_EARLY_BIRD_DISCOUNT_DETAILS, { id });
    const data = response?.data?.list_early_bird_discount_details?.data;
    if (!data?._id) return;

    setEditingId(data._id);
    setValues({
      minAdvanceBookingDays: data?.min_advance_booking_days ?? "",
      startDate: data?.start_date ? moment.unix(data.start_date).format("YYYY-MM-DD") : moment().format("YYYY-MM-DD"),
      endDate: data?.end_date ? moment.unix(data.end_date).format("YYYY-MM-DD") : moment().add(1, "days").format("YYYY-MM-DD"),
      mode: data?.is_rate_fixed ? "fixed" : "percentage",
      discountValue: data?.percentage ?? "",
      fixedRate: data?.fixed_rate ?? "",
    });
    setOpenModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    const isFixedRate = values.mode === "fixed";
    const payload = {
      roomtypeId,
      minAdvanceBookingDays: parseInt(values.minAdvanceBookingDays, 10),
      startDate: moment(values.startDate, "YYYY-MM-DD").unix(),
      endDate: moment(values.endDate, "YYYY-MM-DD").unix(),
      isRateFixed: isFixedRate,
      percentage: isFixedRate ? 0 : Number(values.discountValue || 0),
      fixedRate: isFixedRate ? Number(values.fixedRate || 0) : 0,
    };

    const response = editingId
      ? await authorizedPost(UPDATE_EARLY_BIRD_DISCOUNT, { ...payload, id: editingId })
      : await authorizedPost(ADD_NEW_EARLY_BIRD_DISCOUNT, payload);

    setIsSubmitting(false);
    if (response?.status) {
      await fetchData();
      closeModal();
    }
  };

  const columns = [
    {
      field: "min_advance_booking_days",
      headerName: "Days Before Check-in",
      width: 170,
      valueGetter: ({ row }) => `${row?.min_advance_booking_days ?? 0}+ days`,
    },
    {
      field: "start_date",
      headerName: "Start Date",
      width: 130,
      valueGetter: ({ row }) =>
        row?.start_date ? moment.unix(row.start_date).format("DD-MMM-YYYY") : "-",
    },
    {
      field: "end_date",
      headerName: "End Date",
      width: 130,
      valueGetter: ({ row }) =>
        row?.end_date ? moment.unix(row.end_date).format("DD-MMM-YYYY") : "-",
    },
    {
      field: "discount_value",
      headerName: "Value",
      width: 120,
      renderCell: ({ row }) =>
        row?.is_rate_fixed ? row?.fixed_rate ?? "-" : `${row?.percentage ?? 0}%`,
    },
    {
      field: "is_active",
      headerName: "Active",
      width: 90,
      renderCell: ({ row: { is_active, _id } }) => (
        <AvailabilityUpdation
          isActive={is_active}
          action={async () => {
            await authorizedPost(ACTIVATE_EARLY_BIRD_DISCOUNT, { id: _id });
            fetchData();
          }}
        />
      ),
    },
    {
      field: "edit",
      headerName: "Edit",
      width: 100,
      renderCell: ({ row: { _id } }) => (
        <Button
          size="small"
          variant="outlined"
          startIcon={<Edit fontSize="small" />}
          onClick={() => openForEdit(_id)}
        >
          Edit
        </Button>
      ),
    },
  ];

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="end" mb={1.5}>
        <Typography variant="h4" fontWeight={700}>
          EARLY BIRD DISCOUNT
        </Typography>
        <Button
          sx={{
            backgroundColor: colors.blueAccent[700],
            color: colors.grey[100],
            fontSize: "14px",
            fontWeight: "bold",
            padding: { sm: "10px 20px", xs: "5px 10px" },
          }}
          onClick={() => setOpenModal(true)}
        >
          <Add sx={{ mr: "10px" }} />
          Add new discount
        </Button>
      </Stack>
      <Box height="350px" sx={tableStyle(colors)}>
        <DataGrid rows={discounts} columns={columns} getRowId={(row) => row?._id} />
      </Box>

      <Modal open={openModal} onClose={closeModal}>
        <Box sx={modalStyle}>
          <Header
            title={editingId ? "UPDATE EARLY BIRD DISCOUNT" : "ADD EARLY BIRD DISCOUNT"}
            titleVariant="h4"
          />
          <form onSubmit={handleSubmit}>
            <Stack spacing={2}>
              <CssTextField
                fullWidth
                required
                size="small"
                type="number"
                label="Book at least (days) before check-in"
                value={values.minAdvanceBookingDays}
                inputProps={{ min: 1, step: 1 }}
                onChange={(e) =>
                  setValues((prev) => ({ ...prev, minAdvanceBookingDays: e.target.value }))
                }
              />
              <CssTextField
                fullWidth
                required
                size="small"
                type="date"
                label="Stay start date"
                value={moment(values.startDate).format("YYYY-MM-DD")}
                onChange={(e) => setValues((prev) => ({ ...prev, startDate: e.target.value }))}
                inputProps={{
                  min: moment().format("YYYY-MM-DD"),
                  max: moment().add(720, "days").format("YYYY-MM-DD"),
                }}
                inputRef={startInputRef}
                onClick={() => startInputRef.current.showPicker()}
                InputProps={startIcon}
              />
              <CssTextField
                fullWidth
                required
                size="small"
                type="date"
                label="Stay end date"
                value={moment(values.endDate).format("YYYY-MM-DD")}
                onChange={(e) => setValues((prev) => ({ ...prev, endDate: e.target.value }))}
                inputProps={{
                  min: moment(values.startDate).format("YYYY-MM-DD"),
                  max: moment().add(720, "days").format("YYYY-MM-DD"),
                }}
                inputRef={endInputRef}
                onClick={() => endInputRef.current.showPicker()}
                InputProps={startIcon}
              />

              <RadioGroup
                row
                value={values.mode}
                onChange={(e) => setValues((prev) => ({ ...prev, mode: e.target.value }))}
              >
                <FormControlLabel value="percentage" control={<Radio size="small" />} label="Percentage" />
                <FormControlLabel value="fixed" control={<Radio size="small" />} label="Fixed Rate" />
              </RadioGroup>

              {values.mode === "percentage" ? (
                <CssTextField
                  fullWidth
                  required
                  size="small"
                  type="number"
                  label="Percentage discount"
                  value={values.discountValue}
                  inputProps={{ min: 0.01, max: 100, step: "any" }}
                  onChange={(e) => setValues((prev) => ({ ...prev, discountValue: e.target.value }))}
                />
              ) : (
                <CssTextField
                  fullWidth
                  required
                  size="small"
                  type="number"
                  label="Fixed rate"
                  value={values.fixedRate}
                  inputProps={{ min: 0.01, step: "any" }}
                  onChange={(e) => setValues((prev) => ({ ...prev, fixedRate: e.target.value }))}
                />
              )}

              <Typography variant="body2" color={colors.grey[300]}>
                Applies to bookings made at least the given days before check-in, for nights within the
                stay dates. If a booking engine discount also applies, only the higher discount is given.
              </Typography>

              <Button fullWidth type="submit" color="secondary" variant="contained">
                {isSubmitting ? (
                  <CircularProgress color="inherit" size={22} />
                ) : (
                  <b>{editingId ? "Update" : "Submit"}</b>
                )}
              </Button>
            </Stack>
          </form>
        </Box>
      </Modal>
    </Box>
  );
}

export default RoomtypeEarlyBirdDiscount;
